// Shared visual language for all on-map overlays (Guest Flow, Comfort, Vet Range).
// One palette, one set of opacities/heights, one soft-edged texture — so every overlay reads as
// part of the same system and matches the HUD panels (colors mirror panels.css: pos/warn/neg + teal).
import * as THREE from 'three';

// Semantic palette — identical hexes to panels.css (.zp-pos / .zp-warn / .zp-neg) + Overlays accent.
export const RAMP = {
  good: 0x34d399,  // emerald  — good / calm / high comfort
  mid:  0xfbbf24,  // amber    — caution / busy / ok
  bad:  0xfb7185,  // rose     — problem / congested / low
  idle: 0x7dd3fc,  // sky      — inactive / no data
  care: 0x22d3ee,  // cyan     — categorical (vet coverage), matches panel accent, reads on green terrain
};

// String form for UI legend chips (keep in sync with RAMP).
export const LEGEND_HEX = { good: '#34d399', mid: '#fbbf24', bad: '#fb7185', idle: '#7dd3fc', care: '#22d3ee' };

// Per-overlay legend keys, rendered under each active row in the Overlays panel.
export const LEGENDS = {
  flow:    [['idle', 'Idle'], ['good', 'Calm'], ['mid', 'Busy'], ['bad', 'Packed']],
  comfort: [['bad', 'Low'], ['mid', 'OK'], ['good', 'Great']],
  vet:     [['care', 'In range']],
};

// Unified opacities + ground clearances (tiny per-layer offsets prevent z-fighting when stacked).
export const OVERLAY = {
  tileOpacity: 0.72,
  arrowOpacity: 0.95,
  ringOpacity: 0.72,
  fillOpacity: 0.2,
  liftComfort: 0.18,
  liftFlow: 0.3,
  liftVetFill: 0.12,
  liftVetRing: 0.32,
};

// bad → mid → good ramp. t: 0 = bad, 1 = good. Writes into `out` (a THREE.Color) and returns it.
const _a = new THREE.Color(), _b = new THREE.Color();
export function rampColor(out, t) {
  t = Math.max(0, Math.min(1, t));
  if (t < 0.5) { _a.setHex(RAMP.bad); _b.setHex(RAMP.mid); out.lerpColors(_a, _b, t * 2); }
  else { _a.setHex(RAMP.mid); _b.setHex(RAMP.good); out.lerpColors(_a, _b, (t - 0.5) * 2); }
  return out;
}

// Gentle breathing multiplier for "alive" overlays. Returns lo..hi on a slow sine.
export function pulse(speed = 1, lo = 0.75, hi = 1) {
  const t = 0.5 + 0.5 * Math.sin(performance.now() * 0.001 * speed);
  return lo + (hi - lo) * t;
}

// Soft-edged rounded tile (blurred white rounded-rect on transparent) — turns hard heatmap quads into
// glows that blend between neighbours. instanceColor tints it per cell. Cached singleton.
let _tile = null;
export function softTileTexture() {
  if (_tile) return _tile;
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const x = c.getContext('2d');
  x.filter = 'blur(10px)';
  x.fillStyle = '#fff';
  x.beginPath();
  if (x.roundRect) x.roundRect(20, 20, 88, 88, 26); else x.rect(20, 20, 88, 88);
  x.fill();
  _tile = new THREE.CanvasTexture(c);
  _tile.colorSpace = THREE.SRGBColorSpace;
  return _tile;
}

// Soft radial disc (opaque center → transparent edge) for coverage glows (vet range). Cached singleton.
let _disc = null;
export function softDiscTexture() {
  if (_disc) return _disc;
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const x = c.getContext('2d');
  const g = x.createRadialGradient(64, 64, 4, 64, 64, 64);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.42)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g;
  x.beginPath(); x.arc(64, 64, 64, 0, Math.PI * 2); x.fill();
  _disc = new THREE.CanvasTexture(c);
  _disc.colorSpace = THREE.SRGBColorSpace;
  return _disc;
}

// Clean directional arrow with a soft shadow, on transparent bg (white → tinted by instanceColor).
let _arrow = null;
export function arrowTexture() {
  if (_arrow) return _arrow;
  const c = document.createElement('canvas'); c.width = c.height = 96;
  const x = c.getContext('2d');
  x.shadowColor = 'rgba(0,0,0,0.35)'; x.shadowBlur = 7; x.shadowOffsetY = 2;
  x.fillStyle = '#fff';
  x.beginPath();
  x.moveTo(48, 12);
  x.lineTo(78, 52); x.lineTo(60, 52);
  x.lineTo(60, 84); x.lineTo(36, 84);
  x.lineTo(36, 52); x.lineTo(18, 52);
  x.closePath(); x.fill();
  _arrow = new THREE.CanvasTexture(c);
  _arrow.colorSpace = THREE.SRGBColorSpace;
  return _arrow;
}
